import type { Operation, NextLink, FetchResult } from '@apollo/client';
import { ApolloLink, Observable } from '@apollo/client';
import { getCheckoutTokenFromCookies } from '@auth/utils/tokenUtils';

/**
 * Token resolver function type — matches the signature of AuthContextValue.getAccessToken.
 * This is passed in rather than importing the React context directly so the link
 * can be constructed outside of a React component tree (e.g. in Apollo client setup).
 */
export type GetAccessTokenFn = () => Promise<string | null>;

/**
 * Creates an Apollo Link that attaches auth credentials using the same three-tier
 * fallback strategy as useAuthClient:
 *
 *  1. **Auth0 access token** (Authorization: Bearer) — preferred.
 *  2. **Checkout access token** (Authorization: Bearer) — guest checkout fallback.
 *  3. **credentials: 'include'** — sends session cookies (set on the HttpLink).
 *
 * Most React consumers should reach for the `useApolloAuthClient` hook
 * (`@auth/client/useApolloAuthClient`) instead of calling this factory
 * directly — it wires `getAccessToken` up to `@auth/oauth`'s `useAuth()`
 * automatically and supports splicing the link onto an ApolloClient created
 * outside the React tree (e.g. a module-scoped singleton). Call this factory
 * directly only when you're already outside React and have your own way of
 * resolving the current Auth0 access token.
 *
 * Usage:
 * ```ts
 * import { createApolloAuthLink } from '@auth/client/apolloAuthLink';
 *
 * // In your Apollo client setup (outside React tree):
 * const authLink = createApolloAuthLink(() => authRef.current.getAccessToken());
 *
 * const client = new ApolloClient({
 *   link: ApolloLink.from([authLink, httpLink]),
 *   cache: new InMemoryCache(),
 * });
 * ```
 *
 * The httpLink should be configured with `credentials: 'include'` so that
 * if neither token is available, cookies are still sent as the ultimate fallback.
 */
export const createApolloAuthLink = (getAccessToken: GetAccessTokenFn): ApolloLink => {
  return new ApolloLink(
    (operation: Operation, forward: NextLink): Observable<FetchResult> => {
      return new Observable<FetchResult>(observer => {
        let cancelled = false;
        let innerSubscription: ReturnType<
          ReturnType<typeof forward>['subscribe']
        > | null = null;

        const resolveToken = async (): Promise<string | null> => {
          // --- Priority 1: Auth0 access token ---
          // A rejection here propagates to observer.error via the outer
          // .catch() — callers expect token-fetch failures to surface as
          // GraphQL errors rather than silently downgrading to a guest token.
          const auth0Token = await getAccessToken();
          if (auth0Token) {
            return auth0Token;
          }

          // --- Priority 2: Checkout access token ---
          const checkoutToken = getCheckoutTokenFromCookies();
          if (checkoutToken) {
            return checkoutToken;
          }

          // --- Priority 3: No token — rely on credentials: 'include' on HttpLink ---
          return null;
        };

        resolveToken()
          .then(token => {
            if (cancelled) {
              return;
            }

            if (token) {
              operation.setContext(({ headers = {} }: Record<string, any>) => ({
                headers: {
                  ...headers,
                  Authorization: `Bearer ${token}`,
                },
              }));
            }

            const sub = forward(operation).subscribe({
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            });

            // If unsubscribe was called while forward().subscribe() was
            // executing synchronously (e.g. it fired complete inline), the
            // teardown already ran and couldn't unsubscribe because
            // innerSubscription wasn't assigned yet. Clean up now.
            if (cancelled) {
              sub.unsubscribe();
            } else {
              innerSubscription = sub;
            }
          })
          .catch(err => {
            if (!cancelled) {
              observer.error(err);
            }
          });

        return () => {
          cancelled = true;
          if (innerSubscription) {
            innerSubscription.unsubscribe();
          }
        };
      });
    },
  );
};
